import styled from "styled-components";
import { tokens } from "../../../../public/tokens";
import { BaseButton } from "../../../library/Button";

/*
 * Programme campus/method meta cluster is shared with ModuleReadinessPopover's header via
 * linkedProgrammes.styles.js - both render the same identity block.
 */
export {
    StyledProgrammeDetails,
    StyledProgrammeMetaRow,
    StyledMethod,
} from "./linkedProgrammes.styles";

export const StyledVersionValue = styled.span({
    display: "inline-flex",
    alignItems: "center",

    gap: tokens.globals["spacing-1"],

    color: tokens.colors.brand.blue["500"],

    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: "23px",

    i: {
        fontSize: "12px",
    },
});

export const StyledVersionActiveIcon = styled.i({
    color: tokens.colors.states.success["500"],
});

export const StyledVersionInactiveIcon = styled.i({
    color: tokens.colors.neutral.grey["400"],
});

export const StyledGradeCellContent = styled.div({
    width: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: tokens.globals["spacing-3"],
});

export const StyledGradeStatus = styled.div({
    minWidth: 0,

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",

    gap: tokens.globals["spacing-0-5"],

    overflow: "hidden",
});

export const StyledGradeBadge = styled.span(({ $locked }) => ({
    minHeight: "18px",
    maxHeight: "18px",

    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,

    borderRadius: "10px",

    background: $locked
        ? tokens.colors.brand.naartjie["100"]
        : tokens.colors.neutral.grey.Alpha["10"],

    color: $locked
        ? tokens.colors.states.warning["500"]
        : tokens.colors.text.secondary,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    gap: "4px",

    fontFamily:
        tokens.typography.fontFamilies["albert-sans"],
    fontSize:
        tokens.typography.fontSize["sm-10px"],
    fontWeight:
        tokens.typography.fontWeights.semibold,

    lineHeight: 1,

    whiteSpace: "nowrap",

    i: {
        fontSize: "8px",
    },
}));

export const StyledGradeMeta = styled.span({
    maxWidth: "150px",

    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",

    color: tokens.colors.neutral.grey["500"],

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["xs-8px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "normal",
});

export const StyledGradeAction = styled.button(({ $action }) => ({
    width: "35px",
    height: "35px",

    padding: 0,

    border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],

    background:
        tokens.colors.surfaces["container-background"],

    boxShadow:
        tokens.effects.effects.shadow.default,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    flexShrink: 0,

    cursor: "pointer",

    color:
        $action === "loading"
            ? tokens.colors.brand.blue["500"]
            : $action === "lock"
                ? tokens.colors.states.warning["500"]
                : tokens.colors.neutral.grey["600"],

    i: {
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: tokens.typography.fontSize["lg-14px"],
    },

    "&:hover:not(:disabled)": {
        background:
            tokens.colors.neutral.grey["200"],
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
}));

export const StyledReadinessRow = styled.div({
    width: "100%",
    minWidth: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: tokens.globals["spacing-3"],
});

export const StyledReadinessState = styled.div({
    minWidth: 0,

    display: "flex",
    alignItems: "flex-start",

    gap: tokens.globals["spacing-1-5"],
});

export const StyledReadinessStateIcon = styled.i(({ $ready }) => ({
    width: "14px",
    height: "14px",
    flex: "0 0 14px",
    marginTop: $ready ? "0" : "1px",

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    color: $ready
        ? tokens.colors.states.success["500"]
        : tokens.colors.ui.destructive,

    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: tokens.typography.fontSize["lg-14px"],
}));

export const StyledReadinessText = styled.div({
    minWidth: 0,

    display: "flex",
    flexDirection: "column",
    justifyContent: "center",

    gap: tokens.globals["spacing-0-5"],
});

export const StyledReadinessCount = styled.span({
    color: tokens.colors.text.primary,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: 1.1,

    whiteSpace: "nowrap",
});

export const StyledReadinessWarnings = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],

    flexWrap: "wrap",
});

export const StyledReadinessWarning = styled.span({
    display: "inline-flex",
    alignItems: "center",

    gap: tokens.globals["spacing-0-5"],

    color: tokens.colors.text.secondary,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["sm-10px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "12px",

    whiteSpace: "nowrap",

    i: {
        width: "8px",
        height: "8px",

        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",

        fontSize: "8px",
        lineHeight: "8px",
    },
});

export const StyledModulesButton = styled(BaseButton)(({ $active }) => ({
    height: "35px",
    flex: "0 0 auto",
    whiteSpace: "nowrap",

    padding: `0 ${tokens.globals["spacing-2-5"]}`,

    border: `1px solid ${
        $active
            ? tokens.colors.neutral.black
            : tokens.colors.surfaces["border-divider"]
    }`,

    borderRadius: tokens.globals["border-radius"],

    background: $active
        ? tokens.colors.neutral.black
        : tokens.colors.neutral.white,

    color: $active
        ? tokens.colors.neutral.white
        : tokens.colors.text.primary,

    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.10)",

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    gap: `${tokens.globals["spacing-1-5"]}`,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",

    cursor: "pointer",

    i: {
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: tokens.typography.fontSize["lg-14px"],
    },

    "&:hover:not(:disabled)": {
        background: $active
            ? tokens.colors.neutral.grey["800"]
            : tokens.colors.neutral.grey["200"],
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
}));
